import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  Tv, 
  Film, 
  Sparkles, 
  Layers, 
  Flame, 
  Star, 
  Clock, 
  Play, 
  Search, 
  Filter,
  CheckCircle2,
  Compass,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  FolderPlus
} from 'lucide-react';

import { MediaItem, CuratedCollection, UserProfile, WatchHistoryItem } from './types';
import { 
  auth, 
  subscribeToUserCatalog, 
  subscribeToUserCollections, 
  subscribeToWatchHistory, 
  getUserProfile, 
  logoutUser,
  getCachedCatalog,
  getCachedCollections,
  autoSyncOnStartup,
  getUserCatalogDirect,
  getUserCollections,
  ensureAdminUserCreated
} from './lib/firebase';

import { Navbar } from './components/Navbar';
import { HeroBanner } from './components/HeroBanner';
import { MediaRow } from './components/MediaRow';
import { MediaCard } from './components/MediaCard';
import { MediaDetailModal } from './components/MediaDetailModal';
import { VideoPlayerModal } from './components/VideoPlayerModal';
import { AuthModal } from './components/AuthModal';
import { playMediaUniversal } from './utils/streamUtils';
import { AdminDashboard } from './components/AdminDashboard';
import { UserProfileModal } from './components/UserProfileModal';
import { LoginScreen } from './components/LoginScreen';
import { UserStatusGate } from './components/UserStatusGate';
import { CollectionsView } from './components/CollectionsView';
import { CollectionsRow } from './components/CollectionsRow';
import { BottomNav } from './components/BottomNav';
import { IptvView } from './components/iptv/IptvView';
import { cleanGenres, extractCleanGenreList } from './utils/genreUtils';
import { DeviceModeSelectorModal } from './components/DeviceModeSelectorModal';
import { KeyboardShortcutsModal } from './components/KeyboardShortcutsModal';
import { useDeviceMode } from './hooks/useDeviceMode';
import { useSpatialNavigation } from './hooks/useSpatialNavigation';

export default function App() {
  // Device mode and cross-platform adaptation (PC, Mobile, Android TV, Fire TV)
  const { mode: deviceMode, isTv, isMobile, setMode: setDeviceMode } = useDeviceMode();
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);

  // Global App States - Pre-populated with rich cached data so UI is instantly ready
  const [catalog, setCatalog] = useState<MediaItem[]>(() => getCachedCatalog());
  const [collections, setCollections] = useState<CuratedCollection[]>(() => getCachedCollections());
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [watchHistory, setWatchHistory] = useState<WatchHistoryItem[]>(() => {
    try {
      const saved = localStorage.getItem('garcollections_local_watch_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {}
    return [];
  });
  const [syncState, setSyncState] = useState<{ isSyncing: boolean; statusText: string; count?: number }>({
    isSyncing: false,
    statusText: ''
  });

  // Navigation and Filtering
  const [currentView, setCurrentView] = useState<'home' | 'collections' | 'series' | 'movies' | 'iptv'>('home');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [selectedGenre, setSelectedGenre] = useState<string>('Tots');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'popular' | 'rating' | 'year' | 'title'>('popular');
  const [visibleSeriesLimit, setVisibleSeriesLimit] = useState<number>(24);
  const [visibleMoviesLimit, setVisibleMoviesLimit] = useState<number>(24);

  const seriesSentinelRef = useRef<HTMLDivElement | null>(null);
  const moviesSentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset pagination when filter or view changes for maximum performance
  useEffect(() => {
    setVisibleSeriesLimit(24);
    setVisibleMoviesLimit(24);
  }, [currentView, selectedGenre, sortBy]);

  // Automatic Infinite Scroll / Progressive Loading with IntersectionObserver
  useEffect(() => {
    if (currentView !== 'series' && currentView !== 'movies') return;

    const sentinel = currentView === 'series' ? seriesSentinelRef.current : moviesSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry && entry.isIntersecting) {
        if (currentView === 'series') {
          setVisibleSeriesLimit(prev => prev + 24);
        } else if (currentView === 'movies') {
          setVisibleMoviesLimit(prev => prev + 24);
        }
      }
    }, {
      rootMargin: '600px 0px',
      threshold: 0.01,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [currentView, visibleSeriesLimit, visibleMoviesLimit, selectedGenre, sortBy]);

  // Fallback Window Scroll Listener for automatic progressive loading
  useEffect(() => {
    if (currentView !== 'series' && currentView !== 'movies') return;

    const handleScroll = () => {
      const scrollPos = window.innerHeight + window.scrollY;
      const threshold = document.documentElement.scrollHeight - 700;
      if (scrollPos >= threshold) {
        if (currentView === 'series') {
          setVisibleSeriesLimit(prev => prev + 24);
        } else if (currentView === 'movies') {
          setVisibleMoviesLimit(prev => prev + 24);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentView]);

  // Modals States
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [playingItem, setPlayingItem] = useState<{ item: MediaItem; episodeIndex: number; initialTime?: number } | null>(null);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Unified App Back Action (Modals, Sub-views, Search, Top Bar Focus)
  const handleAppBack = useCallback(() => {
    // 0. Check IPTV player, drawers, dropdowns & sub-views first
    if (typeof (window as any).__handleIptvBack === 'function') {
      const iptvHandled = (window as any).__handleIptvBack();
      if (iptvHandled) return true;
    }

    if (playingItem) {
      if (typeof (window as any).__handleVideoPlayerBack === 'function') {
        const handled = (window as any).__handleVideoPlayerBack();
        if (handled) return true;
      }
      setPlayingItem(null);
      return true;
    }
    if (selectedMedia) {
      const mediaId = selectedMedia.id;
      setSelectedMedia(null);
      setTimeout(() => {
        const card = document.querySelector<HTMLElement>(`#media-card-${mediaId}`);
        if (card) {
          card.focus();
          card.classList.add('tv-focused');
        }
      }, 60);
      return true;
    }
    if (isDeviceModalOpen) {
      setIsDeviceModalOpen(false);
      return true;
    }
    if (isShortcutsModalOpen) {
      setIsShortcutsModalOpen(false);
      return true;
    }
    if (isAdminOpen) {
      setIsAdminOpen(false);
      return true;
    }
    if (isProfileOpen) {
      setIsProfileOpen(false);
      return true;
    }
    if (isAuthOpen) {
      setIsAuthOpen(false);
      return true;
    }
    if (isUserMenuOpen) {
      setIsUserMenuOpen(false);
      const userBtn = document.querySelector<HTMLElement>('#user-profile-button, #login-button');
      if (userBtn) {
        userBtn.focus();
        userBtn.classList.add('tv-focused');
      }
      return true;
    }
    if (isSearchOpen) {
      setIsSearchOpen(false);
      const navHome = document.querySelector<HTMLElement>('#nav-home');
      if (navHome) {
        navHome.focus();
        navHome.classList.add('tv-focused');
      }
      return true;
    }
    if (searchQuery.trim() !== '') {
      setSearchQuery('');
      const navHome = document.querySelector<HTMLElement>('#nav-home');
      if (navHome) {
        navHome.focus();
        navHome.classList.add('tv-focused');
      }
      return true;
    }
    if (selectedCollectionId) {
      setSelectedCollectionId(null);
      return true;
    }
    if (currentView !== 'home') {
      setCurrentView('home');
      const homeTab = document.querySelector<HTMLElement>('#nav-home');
      if (homeTab) {
        homeTab.focus();
        homeTab.classList.add('tv-focused');
      }
      return true;
    }

    const activeEl = document.activeElement as HTMLElement | null;
    if (activeEl?.id !== 'nav-home') {
      const homeTab = document.querySelector<HTMLElement>('#nav-home');
      if (homeTab) {
        homeTab.focus();
        homeTab.classList.add('tv-focused');
        return true;
      }
    }

    return false;
  }, [
    playingItem,
    selectedMedia,
    isDeviceModalOpen,
    isShortcutsModalOpen,
    isAdminOpen,
    isProfileOpen,
    isAuthOpen,
    isUserMenuOpen,
    isSearchOpen,
    searchQuery,
    selectedCollectionId,
    currentView
  ]);

  // Handle Search Submission
  const handleSearchSubmit = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentView('home');
    setIsSearchOpen(false);

    setTimeout(() => {
      const firstResultCard = document.querySelector<HTMLElement>(
        '#search-results-section [data-focusable="true"], #search-results-section .tv-card, [id^="media-card-"]'
      );
      if (firstResultCard) {
        firstResultCard.focus();
        firstResultCard.classList.add('tv-focused');
        firstResultCard.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    }, 100);
  }, []);

  // Unified Watch Progress Synchronizer (LocalStorage + Android Native ExoPlayer)
  const refreshWatchProgress = useCallback(() => {
    try {
      // 1. Check localStorage
      const saved = localStorage.getItem('garcollections_local_watch_history');
      let localItems: WatchHistoryItem[] = saved ? JSON.parse(saved) : [];
      if (!Array.isArray(localItems)) localItems = [];

      // 2. Check Android Native ExoPlayer saved progress
      if (typeof window !== 'undefined' && (window as any).Android && typeof (window as any).Android.getAllSavedProgress === 'function') {
        const raw = (window as any).Android.getAllSavedProgress();
        const androidItems = JSON.parse(raw);
        if (Array.isArray(androidItems) && androidItems.length > 0) {
          const map = new Map<string, WatchHistoryItem>();
          for (const item of localItems) map.set(item.mediaId, item);
          for (const aItem of androidItems) {
            const existing = map.get(aItem.mediaId);
            const aTime = aItem.lastWatchedAt ? new Date(aItem.lastWatchedAt).getTime() : ((aItem as any).timestamp || 0);
            const exTime = existing?.lastWatchedAt ? new Date(existing.lastWatchedAt).getTime() : 0;
            if (!existing || aTime >= exTime) {
              map.set(aItem.mediaId, { ...existing, ...aItem });
            }
          }
          localItems = Array.from(map.values());
        }
      }

      if (localItems.length > 0) {
        localItems.sort((a, b) => new Date(b.lastWatchedAt || 0).getTime() - new Date(a.lastWatchedAt || 0).getTime());
        setWatchHistory(prev => {
          const merged = [...prev];
          for (const item of localItems) {
            const idx = merged.findIndex(m => m.mediaId === item.mediaId);
            if (idx >= 0) {
              merged[idx] = { ...merged[idx], ...item };
            } else {
              merged.push(item);
            }
          }
          merged.sort((a, b) => new Date(b.lastWatchedAt || 0).getTime() - new Date(a.lastWatchedAt || 0).getTime());
          return merged;
        });
      }
    } catch (e) {
      console.warn("Error refreshing watch progress:", e);
    }
  }, []);

  // Expose to window for Android WebView Native Bridge
  useEffect(() => {
    (window as any).__handleGarCollectonisBack = handleAppBack;
    (window as any).__handleGarCollectionsBack = handleAppBack;
    (window as any).__handleAnimeCatBack = handleAppBack;
    (window as any).__refreshWatchProgress = refreshWatchProgress;

    // Refresh on mount and when app regains focus (e.g. returning from native ExoPlayer)
    refreshWatchProgress();
    window.addEventListener('focus', refreshWatchProgress);
    document.addEventListener('visibilitychange', refreshWatchProgress);

    return () => {
      delete (window as any).__handleGarCollectonisBack;
      delete (window as any).__handleGarCollectionsBack;
      delete (window as any).__handleAnimeCatBack;
      delete (window as any).__refreshWatchProgress;
      window.removeEventListener('focus', refreshWatchProgress);
      document.removeEventListener('visibilitychange', refreshWatchProgress);
    };
  }, [handleAppBack, refreshWatchProgress]);

  // Spatial Navigation
  useSpatialNavigation({
    enabled: true,
    isTv: isTv,
    onBack: handleAppBack
  });

  // Background Synchronization
  const handleAppRefresh = useCallback(async () => {
    setSyncState({ isSyncing: true, statusText: 'Sincronitzant col·leccions...' });
    try {
      if (user) {
        await autoSyncOnStartup(
          user.uid,
          (progress) => {
            setSyncState({
              isSyncing: !progress.done,
              statusText: progress.stage,
              count: progress.count
            });
          },
          true // forceRemoteSync = true
        );
        const items = await getUserCatalogDirect(user.uid);
        if (items && items.length > 0) {
          setCatalog(items);
        }
        const userCols = await getUserCollections(user.uid);
        if (userCols && userCols.length > 0) {
          setCollections(userCols);
        }
      } else {
        await autoSyncOnStartup(
          undefined,
          (progress) => {
            setSyncState({
              isSyncing: !progress.done,
              statusText: progress.stage,
              count: progress.count
            });
          },
          true // forceRemoteSync = true
        );
      }

      // Check native Android watch progress
      if (typeof window !== 'undefined' && (window as any).Android && typeof (window as any).Android.getAllSavedProgress === 'function') {
        try {
          const raw = (window as any).Android.getAllSavedProgress();
          const items = JSON.parse(raw);
          if (Array.isArray(items) && items.length > 0) {
            setWatchHistory(prev => {
              const merged = [...prev];
              for (const item of items) {
                const existingIdx = merged.findIndex(m => m.mediaId === item.mediaId);
                const itemTime = item.lastWatchedAt ? new Date(item.lastWatchedAt).getTime() : ((item as any).timestamp || 0);
                if (existingIdx >= 0) {
                  const existingTime = merged[existingIdx].lastWatchedAt ? new Date(merged[existingIdx].lastWatchedAt).getTime() : 0;
                  if (itemTime > existingTime) {
                    merged[existingIdx] = { ...merged[existingIdx], ...item };
                  }
                } else {
                  merged.unshift(item);
                }
              }
              return merged;
            });
          }
        } catch (e) {
          console.warn("Error parsing android watch progress:", e);
        }
      }
    } catch (err) {
      console.warn("Refresh catalog warning:", err);
    } finally {
      setSyncState({ isSyncing: false, statusText: 'Sincronitzat' });
      setTimeout(() => {
        setSyncState(prev => ({ ...prev, statusText: '' }));
      }, 3000);
    }
  }, [user]);

  // Auth & Admin User Initialization Listener
  useEffect(() => {
    // Ensure the system admin document exists in Firestore (garcollections2)
    ensureAdminUserCreated().catch(console.warn);

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          let profile = await getUserProfile(firebaseUser.uid);
          
          // If logged in as admin or email is admin@animecat.cat
          if (!profile && (firebaseUser.email === 'admin@animecat.cat' || firebaseUser.displayName === 'admin')) {
            profile = await getUserProfile('admin');
          }

          if (profile) {
            // Assign admin privileges if matching admin identities
            const isAdmin = profile.role === 'admin' || 
                            profile.username === 'admin' || 
                            firebaseUser.email === 'josep.g.frigole2@gmail.com' || 
                            firebaseUser.email === 'admin@animecat.cat';
            if (isAdmin) {
              profile.role = 'admin';
              profile.status = 'active';
            }
            setUser(profile);
          } else {
            const isAdmin = firebaseUser.email === 'josep.g.frigole2@gmail.com' || 
                            firebaseUser.email === 'admin@animecat.cat' ||
                            firebaseUser.displayName === 'admin';
            setUser({
              uid: firebaseUser.uid,
              username: firebaseUser.displayName || 'Usuari',
              displayName: firebaseUser.displayName || 'Usuari GarCollections',
              email: firebaseUser.email || '',
              avatar: firebaseUser.photoURL || 'https://images.unsplash.com/photo-1534447677768-be436bb09401?w=150&auto=format&fit=crop&q=80',
              role: isAdmin ? 'admin' : 'user',
              status: isAdmin ? 'active' : 'pending',
              createdAt: new Date().toISOString()
            });
          }
        } else {
          // If no Firebase Auth user, but user had logged in via custom Firestore credentials
          setUser(prev => (prev?.username === 'admin' && prev?.role === 'admin' ? prev : null));
          setWatchHistory([]);
        }
      } catch (err) {
        console.error("Auth status error:", err);
      } finally {
        setIsAuthChecking(false);
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  // Per-User Data Subscriptions (Catalog, Collections, Watch History)
  useEffect(() => {
    if (!user) {
      return;
    }

    const unsubCatalog = subscribeToUserCatalog(user.uid, (items) => {
      setCatalog(items);
    });

    const unsubCollections = subscribeToUserCollections(user.uid, (cols) => {
      setCollections(cols);
    });

    const unsubHistory = subscribeToWatchHistory(user.uid, (items) => {
      setWatchHistory(items);
    });

    return () => {
      unsubCatalog();
      unsubCollections();
      unsubHistory();
    };
  }, [user]);

  // History Map
  const historyMap: Record<string, WatchHistoryItem> = useMemo(() => {
    const map: Record<string, WatchHistoryItem> = {};
    watchHistory.forEach(h => {
      map[h.mediaId] = h;
      if (h.season && h.episode) {
        map[`${h.mediaId}_s${h.season}_e${h.episode}`] = h;
      }
    });
    return map;
  }, [watchHistory]);

  // Play Action Handler
  const handlePlayMedia = (item: MediaItem, episodeIndex: number = 0, initialTime?: number) => {
    let targetEpIndex = episodeIndex;
    let resumeTime = initialTime;
    const h = historyMap[item.id];

    if (resumeTime === undefined) {
      if (item.type === 'series' && item.episodes && item.episodes.length > 0) {
        // If no explicit episode was selected (e.g. general Play on card where episodeIndex is 0 and initialTime undefined)
        if (episodeIndex === 0 && initialTime === undefined && h && h.progressSeconds > 0) {
          if (h.episode) {
            const foundIdx = item.episodes.findIndex(
              (ep, idx) => (h.season ? (ep.season || 1) === h.season : true) && 
                           (ep.episode === h.episode || (idx + 1) === h.episode)
            );
            if (foundIdx >= 0) {
              targetEpIndex = foundIdx;
              resumeTime = h.progressSeconds;
            }
          }
        } else {
          // Specific episode selected: check progress for that specific episode
          const ep = item.episodes[targetEpIndex];
          const epHistory = ep ? historyMap[`${item.id}_s${ep.season || 1}_e${ep.episode}`] : null;
          if (epHistory && epHistory.progressSeconds > 0) {
            resumeTime = epHistory.progressSeconds;
          } else if (ep && h && (h.episode === ep.episode || h.episode === targetEpIndex + 1)) {
            resumeTime = h.progressSeconds;
          }
        }
      } else if (h && h.progressSeconds > 0) {
        resumeTime = h.progressSeconds;
      }
    }

    // Play Media via Android Native ExoPlayer or Web Player fallback
    playMediaUniversal(
      item,
      targetEpIndex,
      resumeTime || 0,
      (fallbackItem, fallbackEpIdx, fallbackResume) => {
        setPlayingItem({ item: fallbackItem, episodeIndex: fallbackEpIdx, initialTime: fallbackResume });
      }
    );
  };

  const handleLogout = async () => {
    await logoutUser();
    setUser(null);
  };

  // Filtered & Sorted Lists
  const allGenres = useMemo(() => {
    return extractCleanGenreList(catalog);
  }, [catalog]);

  const getFilteredItems = (typeFilter?: 'series' | 'movie') => {
    return catalog.filter(item => {
      const matchesType = !typeFilter || item.type === typeFilter;
      const itemCleanGenres = cleanGenres(item.genres);
      const matchesGenre = selectedGenre === 'Tots' || itemCleanGenres.includes(selectedGenre);
      const matchesSearch = !searchQuery || 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.originalTitle && item.originalTitle.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.summary && item.summary.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesType && matchesGenre && matchesSearch;
    }).sort((a, b) => {
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'year') return parseInt(b.year || '0') - parseInt(a.year || '0');
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
    });
  };

  const featuredItems = catalog.filter(item => item.featured).length > 0
    ? catalog.filter(item => item.featured)
    : catalog.slice(0, 3);

  const seriesItems = catalog.filter(item => item.type === 'series');
  const movieItems = catalog.filter(item => item.type === 'movie');

  // Search Results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    return catalog.filter(item => {
      const titleMatch = item.title.toLowerCase().includes(q);
      const origMatch = item.originalTitle?.toLowerCase().includes(q);
      const summaryMatch = item.summary?.toLowerCase().includes(q);
      const genreMatch = item.genres?.some(g => g.toLowerCase().includes(q));
      return titleMatch || origMatch || summaryMatch || genreMatch;
    });
  }, [catalog, searchQuery]);

  // Continue watching items (limit to 12 most recent in-progress items)
  const continueWatchingMedia = useMemo(() => {
    const seenMediaIds = new Set<string>();
    const list: MediaItem[] = [];

    for (const h of watchHistory) {
      // Must have started (at least 5s) and not finished (>92% or explicitly completed)
      const isFinished = h.completed || (h.durationSeconds > 0 && (h.progressSeconds / h.durationSeconds) >= 0.92);
      if (isFinished || (h.progressSeconds || 0) < 5) continue;
      if (seenMediaIds.has(h.mediaId)) continue;

      const media = catalog.find(c => 
        c.id === h.mediaId || 
        (c.tmdbId && h.tmdbId && String(c.tmdbId) === String(h.tmdbId)) ||
        c.title.trim().toLowerCase() === (h.mediaTitle || '').trim().toLowerCase() ||
        (h.mediaTitle && c.title.toLowerCase().includes(h.mediaTitle.toLowerCase())) ||
        (h.mediaTitle && h.mediaTitle.toLowerCase().includes(c.title.toLowerCase()))
      );

      if (media) {
        seenMediaIds.add(h.mediaId);
        list.push(media);
        if (list.length >= 12) break;
      } else if (h.mediaTitle) {
        seenMediaIds.add(h.mediaId);
        list.push({
          id: h.mediaId,
          title: h.mediaTitle,
          originalTitle: h.mediaTitle,
          summary: '',
          tmdbId: h.tmdbId || 0,
          type: (h.type as any) || 'movie',
          poster: h.mediaPoster || '',
          backdrop: h.mediaPoster || '',
          streamUrl: h.streamUrl || '',
          genres: ['Continua Veient'],
          rating: 8.0,
          year: '2024'
        });
        if (list.length >= 12) break;
      }
    }
    return list;
  }, [watchHistory, catalog]);

  // 1. Loading screen
  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-[#070b14] text-slate-100 flex flex-col items-center justify-center p-4">
        <div className="flex flex-col items-center gap-4 animate-in fade-in duration-300">
          <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 flex items-center justify-center shadow-2xl shadow-sky-500/30 animate-pulse">
            <Layers className="w-7 h-7 text-white" />
            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-cyan-400 border-2 border-black flex items-center justify-center">
              <Sparkles className="w-2.5 h-2.5 text-black" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-display font-black text-2xl tracking-tight text-white">
              Gar<span className="text-sky-400">Collections</span>
            </span>
          </div>
          <div className="w-6 h-6 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mt-2" />
        </div>
      </div>
    );
  }

  // 2. Strict Auth Wall: Require login to view any content
  if (!user) {
    return (
      <>
        <LoginScreen onSuccess={(loggedUser) => setUser(loggedUser)} />
        <AdminDashboard
          isOpen={isAdminOpen}
          onClose={() => setIsAdminOpen(false)}
          currentUser={null}
          catalog={catalog}
          collections={collections}
          onRefreshCatalog={handleAppRefresh}
        />
      </>
    );
  }

  // 3. User Status Gate: Pending or Blocked users require admin activation
  if (user.role !== 'admin' && (user.status === 'pending' || user.status === 'blocked')) {
    return (
      <>
        <UserStatusGate
          user={user}
          onRefresh={(updated) => setUser(updated)}
          onLogout={handleLogout}
          onOpenAdmin={() => setIsAdminOpen(true)}
        />
        <AdminDashboard
          isOpen={isAdminOpen}
          onClose={() => setIsAdminOpen(false)}
          currentUser={user}
          catalog={catalog}
          collections={collections}
          onRefreshCatalog={handleAppRefresh}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100 flex flex-col selection:bg-sky-500 selection:text-black">
      
      {/* Top Fixed Navbar */}
      <Navbar
        currentView={currentView}
        onNavigate={(view) => {
          setCurrentView(view as any);
          if (view !== 'collections') {
            setSelectedCollectionId(null);
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        user={user}
        collections={collections}
        selectedCollectionId={selectedCollectionId}
        onSelectCollection={(colId) => {
          setSelectedCollectionId(colId);
          setCurrentView('collections');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onOpenAuth={() => setIsAuthOpen(true)}
        onLogout={handleLogout}
        onOpenAdmin={() => setIsAdminOpen(true)}
        onOpenProfile={() => setIsProfileOpen(true)}
        catalog={catalog}
        onSelectMedia={(item) => setSelectedMedia(item)}
        deviceMode={deviceMode}
        isTv={isTv}
        onOpenDeviceSelector={() => setIsDeviceModalOpen(true)}
        onOpenShortcuts={() => setIsShortcutsModalOpen(true)}
        onRefresh={handleAppRefresh}
        isSyncing={syncState.isSyncing}
        syncStatusText={syncState.statusText}
        isSearchOpenExternal={isSearchOpen}
        onToggleSearchExternal={(open) => setIsSearchOpen(open)}
        isUserMenuOpenExternal={isUserMenuOpen}
        onToggleUserMenuExternal={(open) => setIsUserMenuOpen(open)}
        onSearchSubmit={handleSearchSubmit}
      />

      {/* Main Content View Switcher */}
      <main className="flex-1 pb-20 md:pb-12">
        
        {/* ======================================================== */}
        {/* VIEW 1: HOME (Inici)                                     */}
        {/* ======================================================== */}
        {currentView === 'home' && (
          <div>
            {searchQuery.trim() !== '' ? (
              <div id="search-results-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 space-y-6">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-sky-500 text-black">
                        CERCA
                      </span>
                      <h1 className="font-display font-black text-2xl sm:text-3xl text-white">
                        Resultats per &ldquo;{searchQuery}&rdquo;
                      </h1>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-400 mt-1">
                      {searchResults.length} títol{searchResults.length !== 1 ? 's' : ''} trobat{searchResults.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <button
                    id="clear-search-btn"
                    data-focusable="true"
                    onClick={() => {
                      setSearchQuery('');
                      const homeTab = document.querySelector<HTMLElement>('#nav-home');
                      if (homeTab) {
                        homeTab.focus();
                        homeTab.classList.add('tv-focused');
                      }
                    }}
                    className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold text-white border border-white/10 transition-colors cursor-pointer"
                  >
                    Netejar cerca
                  </button>
                </div>

                {searchResults.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 bg-white/5 rounded-2xl border border-white/5">
                    <p className="text-base font-semibold text-slate-300">No s'ha trobat cap títol coincident amb &ldquo;{searchQuery}&rdquo;.</p>
                    <p className="text-xs text-slate-500 mt-1">Prova amb paraules clau com el títol d'una sèrie o pel·lícula.</p>
                  </div>
                ) : (
                  <MediaRow
                    id="row-search-results"
                    title={`Resultats per "${searchQuery}"`}
                    items={searchResults}
                    onSelect={(i) => setSelectedMedia(i)}
                    onPlay={handlePlayMedia}
                    historyMap={historyMap}
                  />
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {/* Featured Hero Banner */}
                <HeroBanner
                  items={featuredItems}
                  onPlay={handlePlayMedia}
                  onOpenDetails={(item) => setSelectedMedia(item)}
                />

                {/* 1. Seguir veient (Per a la TV i Web: a la TV limitat a 5 títols exactes, ubicat a dalt de tot) */}
                {continueWatchingMedia.length > 0 && (
                  <MediaRow
                    id="row-continue-watching"
                    title="Seguir veient"
                    subtitle={isTv ? "Reprèn la reproducció exactament on ho vas deixar" : "Reprèn els teus títols allà on ho vas deixar"}
                    icon={<Clock className="w-5 h-5 text-sky-400" />}
                    items={continueWatchingMedia.slice(0, isTv ? 5 : 12)}
                    maxLimit={isTv ? 5 : 12}
                    directPlayOnSelect={true}
                    onSelect={(item) => {
                      const h = historyMap[item.id];
                      let epIdx = 0;
                      if (item.type === 'series' && item.episodes && h?.episode) {
                        const idx = item.episodes.findIndex(
                          (e, i) => (h.season ? (e.season || 1) === h.season : true) && 
                                    (e.episode === h.episode || (i + 1) === h.episode)
                        );
                        if (idx >= 0) epIdx = idx;
                      }
                      handlePlayMedia(item, epIdx, h?.progressSeconds || 0);
                    }}
                    onPlay={handlePlayMedia}
                    historyMap={historyMap}
                  />
                )}

                {/* 2. Vista prèvia de Col·leccions (Un únic item per col·lecció) */}
                {collections.length > 0 && (
                  <CollectionsRow
                    collections={collections}
                    catalog={catalog}
                    onSelectCollection={(colId) => {
                      setSelectedCollectionId(colId);
                      setCurrentView('collections');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onViewAll={() => {
                      setSelectedCollectionId(null);
                      setCurrentView('collections');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  />
                )}

                {/* 3. Pel·lícules */}
                {movieItems.length > 0 && (
                  <MediaRow
                    id="row-movies-home"
                    title="Pel·lícules"
                    subtitle="Llargmetratges i cinema disponible"
                    icon={<Film className="w-5 h-5 text-cyan-400" />}
                    items={movieItems}
                    maxLimit={20}
                    onViewAll={() => {
                      setCurrentView('movies');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onSelect={(item) => setSelectedMedia(item)}
                    onPlay={handlePlayMedia}
                    historyMap={historyMap}
                  />
                )}

                {/* 4. Sèries */}
                {seriesItems.length > 0 && (
                  <MediaRow
                    id="row-popular-series"
                    title="Sèries"
                    subtitle="Totes les temporades i episodis disponibles"
                    icon={<Tv className="w-5 h-5 text-sky-400" />}
                    items={seriesItems}
                    maxLimit={20}
                    onViewAll={() => {
                      setCurrentView('series');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onSelect={(item) => setSelectedMedia(item)}
                    onPlay={handlePlayMedia}
                    historyMap={historyMap}
                  />
                )}

                {catalog.length === 0 && (
                  <div className="max-w-md mx-auto my-12 text-center p-8 rounded-3xl bg-[#0c1220] border border-white/10 space-y-4">
                    <div className="w-14 h-14 rounded-2xl bg-sky-500/20 text-sky-400 flex items-center justify-center mx-auto">
                      <FolderPlus className="w-7 h-7" />
                    </div>
                    <h3 className="font-display font-bold text-xl text-white">El teu catàleg està buit</h3>
                    <p className="text-xs text-slate-400">
                      Obre el panell de gestió per carregar el teu primer fitxer JSON i crear col·leccions automàticament.
                    </p>
                    <button
                      onClick={() => setIsAdminOpen(true)}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs sm:text-sm shadow-md transition-all"
                    >
                      Carregar Fitxer JSON
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ======================================================== */}
        {/* VIEW 2: COLLECTIONS (Col·leccions per fitxer JSON)       */}
        {/* ======================================================== */}
        {currentView === 'collections' && (
          <CollectionsView
            collections={collections}
            catalog={catalog}
            selectedCollectionId={selectedCollectionId}
            onSelectCollection={(colId) => setSelectedCollectionId(colId)}
            onSelectMedia={(item) => setSelectedMedia(item)}
            onPlayMedia={handlePlayMedia}
            historyMap={historyMap}
          />
        )}

        {/* ======================================================== */}
        {/* VIEW 3: SERIES (Sèries)                                  */}
        {/* ======================================================== */}
        {currentView === 'series' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 space-y-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-white/10 pb-6">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-blue-500 text-white">
                    SÈRIES
                  </span>
                  <h1 className="font-display font-black text-3xl sm:text-4xl text-white">
                    Totes les Sèries
                  </h1>
                </div>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  Explora totes les temporades i episodis del teu catàleg.
                </p>
              </div>

              {/* Controls */}
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-xs text-white focus:outline-none"
                >
                  {allGenres.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>

                <select
                  value={sortBy}
                  onChange={(e: any) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-xs text-white focus:outline-none"
                >
                  <option value="popular">Més Populars</option>
                  <option value="rating">Millor Puntuats</option>
                  <option value="year">Més Recents</option>
                  <option value="title">Alfabètic (A-Z)</option>
                </select>
              </div>
            </div>

            {/* Lateral single row of Series */}
            {(() => {
              const seriesList = getFilteredItems('series');
              return (
                <>
                  <MediaRow
                    id="row-series-filtered"
                    title={selectedGenre === 'Tots' ? "Totes les Sèries" : `Sèries - ${selectedGenre}`}
                    icon={<Tv className="w-5 h-5 text-blue-400" />}
                    items={seriesList}
                    onSelect={(i) => setSelectedMedia(i)}
                    onPlay={handlePlayMedia}
                    historyMap={historyMap}
                  />

                  {seriesList.length === 0 && (
                    <div className="text-center py-16 text-slate-400 space-y-2">
                      <Tv className="w-12 h-12 text-slate-600 mx-auto" />
                      <p className="font-semibold text-white">No s'han trobat sèries amb aquest filtre.</p>
                      <p className="text-xs">Prova de canviar el gènere o afegeix-ne al Panell de Gestió.</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ======================================================== */}
        {/* VIEW 4: MOVIES (Pel·lícules)                             */}
        {/* ======================================================== */}
        {currentView === 'movies' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 space-y-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-white/10 pb-6">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-cyan-500 text-black">
                    CINEMA
                  </span>
                  <h1 className="font-display font-black text-3xl sm:text-4xl text-white">
                    Pel·lícules
                  </h1>
                </div>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  Llargmetratges i cinema en streaming del teu catàleg.
                </p>
              </div>

              {/* Controls */}
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-xs text-white focus:outline-none"
                >
                  {allGenres.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>

                <select
                  value={sortBy}
                  onChange={(e: any) => setSortBy(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-xs text-white focus:outline-none"
                >
                  <option value="popular">Més Populars</option>
                  <option value="rating">Millor Puntuats</option>
                  <option value="year">Més Recents</option>
                  <option value="title">Alfabètic (A-Z)</option>
                </select>
              </div>
            </div>

            {/* Lateral single row of Movies */}
            {(() => {
              const moviesList = getFilteredItems('movie');
              return (
                <>
                  <MediaRow
                    id="row-movies-filtered"
                    title={selectedGenre === 'Tots' ? "Totes les Pel·lícules" : `Pel·lícules - ${selectedGenre}`}
                    icon={<Film className="w-5 h-5 text-cyan-400" />}
                    items={moviesList}
                    onSelect={(i) => setSelectedMedia(i)}
                    onPlay={handlePlayMedia}
                    historyMap={historyMap}
                  />

                  {moviesList.length === 0 && (
                    <div className="text-center py-16 text-slate-400 space-y-2">
                      <Film className="w-12 h-12 text-slate-600 mx-auto" />
                      <p className="font-semibold text-white">No s'han trobat pel·lícules amb aquest filtre.</p>
                      <p className="text-xs">Prova de canviar el gènere o importa'n des del panell de gestió.</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ======================================================== */}
        {/* VIEW 5: IPTV (TV en Directe & Catàleg VOD IPTV)          */}
        {/* ======================================================== */}
        {currentView === 'iptv' && (
          <IptvView
            userId={user.uid}
            onOpenAdmin={() => setIsAdminOpen(true)}
          />
        )}

      </main>

      {/* Media Detail Modal */}
      {selectedMedia && (
        <MediaDetailModal
          item={selectedMedia}
          onClose={() => setSelectedMedia(null)}
          onPlay={(item, epIdx, initTime) => {
            setSelectedMedia(null);
            handlePlayMedia(item, epIdx, initTime);
          }}
          historyMap={historyMap}
        />
      )}

      {/* Fullscreen Video Player Modal */}
      {playingItem && (
        <VideoPlayerModal
          item={playingItem.item}
          initialEpisodeIndex={playingItem.episodeIndex}
          initialTime={playingItem.initialTime}
          onClose={() => setPlayingItem(null)}
          user={user}
        />
      )}

      {/* Authentication Modal */}
      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onSuccess={(loggedUser) => setUser(loggedUser)}
      />

      {/* User Profile Config Modal */}
      <UserProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        user={user}
        onUserUpdated={(u) => setUser(u)}
      />

      {/* Admin Content & JSON Importer Dashboard */}
      <AdminDashboard
        isOpen={isAdminOpen}
        onClose={() => setIsAdminOpen(false)}
        currentUser={user}
        catalog={catalog}
        collections={collections}
        onRefreshCatalog={handleAppRefresh}
      />

      {/* Mobile Floating / Bottom Navigation Bar */}
      {!playingItem && (
        <BottomNav
          currentView={currentView}
          onNavigate={(view) => {
            setCurrentView(view as any);
            if (view !== 'collections') {
              setSelectedCollectionId(null);
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onOpenSearch={() => {
            const searchInput = document.getElementById('navbar-search-input');
            if (searchInput) {
              searchInput.focus();
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          collectionsCount={collections.length}
          deviceMode={deviceMode}
          onToggleTvMode={() => setDeviceMode(isTv ? 'auto' : 'tv')}
        />
      )}

      {/* Device Optimization Switcher Modal */}
      <DeviceModeSelectorModal
        isOpen={isDeviceModalOpen}
        onClose={() => setIsDeviceModalOpen(false)}
        currentMode={deviceMode}
        onSelectMode={(mode) => setDeviceMode(mode)}
        onOpenShortcuts={() => setIsShortcutsModalOpen(true)}
      />

      {/* Keyboard & TV Remote Shortcuts Guide Modal */}
      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
        isTv={isTv}
      />

    </div>
  );
}
